import HighchartsReact from "highcharts-react-official";
import { groupBy } from "lodash";
import * as React from "react";
import * as Highcharts from "highcharts/highstock";
import { PaymentPayerMapper, PaymentTypeMapper } from "../../Constant/Content";
import { SubAccountType } from "../../../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { JsonDateParser } from "../../../../../../../../../react-core/Utils/JsonDateParser";
import { PaymentPayer } from "../../../../../../../../../Wrap/Constants/Enums/Payments/PaymentPayer";
import AriaTooltip from "../../../../../../../../Shared/Tooltip/AriaTooltip";
import { MoneyDisplayHelper } from "../../../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CurrencyCode } from "../../../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { Table } from "../../../../../../../../Shared/Table/Table";
import { addMonths, addYears, addDays } from "../../../../../../../../../react-core/utils/date";
import { ProductOverviewPaymentsInAction } from "../../Action/ProductOverviewPaymentsInAction";
import { SelectEditor } from "../../../../../../../../../react-core/Html/SelectEditor";
import { SubAccountTypeHelper } from "../../../../../Common/Helpers/SubAccountTypeHelper";
import { PaymentSubType } from "../../../../../../../../../Wrap/Constants/Enums/Apply/PaymentSubType";
import { ExcelExportButton } from "../../../../../../../../Shared/ExcelExport/ExcelExportButton";
import { ExcelExportAction } from "../../../../../../../../Shared/ExcelExport/Actions/ExcelExportAction";
import { ContextStore } from "../../../../../../../../../react-core/Flux/Variables";
import { ContextType } from "../../../../../../../../../Wrap/Constants/Enums/ContextType";
import AdviserSiteTagManager, { eventCategoryForGTM } from "../../../../../../../../Shared/Cookies/ChoicesCookie/AdviserSiteTagManager";

interface ICompletedInstructionsProps {
    subAccountHierarchyId: string;
    selectedProduct: SubAccountType;
}

interface IChartAndTableProps extends ICompletedInstructionsProps {
    fromDate?: Date;
    fromInception: boolean;
}

const options = (data: any): Highcharts.Options => {
    return {
        chart: {
            renderTo: "container",
            type: "column",
            style: {
                fontFamily: "inherit",
            },
            height: 250,
            backgroundColor: "transparent",
            className: "instructions-column-chart-isa",
        },
        title: {
            text: "",
        },
        credits: {
            enabled: false,
        },
        xAxis: {
            crosshair: true,
            lineColor: "transparent",
            tickWidth: 0,
            tickLength: 0,
            tickColor: "#979797",
            type: "datetime",
            showFirstLabel: true,
            showLastLabel: true,
            gridLineWidth: 0,
            labels: {
                useHTML: true,
                style: {
                    color: "#0a2f73",
                },
                align: "center",
                enabled: true,
                step: 1,
                formatter: function () {
                    const date = new Date(this.value);
                    const month = JsonDateParser.getMonthNameMapping((date.getMonth() + 1).toString(), true);
                    const year = JsonDateParser.getFormatDate(JsonDateParser.toJsonDateNoOffset(date), "yy");

                    const formatDate = month + " " + year;
                    return formatDate;
                },
            },
            title: {
                text: "",
            },
            minRange: 24 * 3600 * 1000 * 365,
        },
        yAxis: {
            maxPadding: 0,
            allowDecimals: false,
            gridLineWidth: 0,
            title: null,
            labels: {
                formatter: function () {
                    return "£" + this.value;
                },
                style: {
                    color: "#0a2f73",
                },
                step: 1,
                useHTML: true,
            },
        },
        tooltip: {
            formatter: function () {
                return "<b>" + this.series.name + "</b>: £" + this.y;
            },
            borderColor: "transparent",
            useHTML: true,
        },
        legend: {
            symbolHeight: 14,
            symbolWidth: 14,
            symbolRadius: 0,
            symbolPadding: 10,
            useHTML: true,
            itemStyle: { lineHeight: "14px" },
        },
        plotOptions: {
            column: {
                stacking: "normal",
                dataLabels: {
                    enabled: false,
                },
                states: {
                    inactive: {
                        opacity: 1,
                    },
                },
            },
        },
        series: composeDataSeries(data),
    };
};

const renderChartAndTable = (data: any, props: IChartAndTableProps) => {
    const tableData = composeTableData(data);
    let singleTotal = 0,
        regularTotal = 0,
        singleTaxReliefTotal = 0,
        regularTaxReliefTotal = 0,
        allTotal = 0;
    tableData.forEach((element) => {
        singleTotal += element["Single"] || 0;
        regularTotal += element["Regular"] || 0;
        singleTaxReliefTotal += element["SingleTaxRelief"] || 0;
        regularTaxReliefTotal += element["RegularTaxRelief"] || 0;
        allTotal += element["Total"] || 0;
    });

    const addEvent = new AdviserSiteTagManager().addEvent;

    const cols = [
        {
            title: "Payer",
            key: "Payer",
            thCssWrapper: "cell-right",
            tdCssWrapper: "cell-right",
            render: (data) => {
                if (data === null || data === "" || data === PaymentPayer.NotSpecified) {
                    return (
                        <AriaTooltip
                            cssWrapper="is-underlined-text no-border-radius"
                            buttonText={"No recorded Payee"}
                            showCloseButton={true}
                        >
                            We don’t have a record of the payee for this transaction
                        </AriaTooltip>
                    );
                } else {
                    return PaymentPayerMapper[data];
                }
            },
        },
        {
            title: "Single payment",
            key: "Single",
            thCssWrapper: "cell-right",
            tdCssWrapper: "cell-right",
            render: (data) => {
                return MoneyDisplayHelper.FromatMoneyDisplay(data || 0, CurrencyCode.GBP, true, 2);
            },
        },
    ];

    const totalBody = [
        { data: "Total", colSpan: 1 },
        {
            data: MoneyDisplayHelper.FromatMoneyDisplay(singleTotal, CurrencyCode.GBP, true, 2),
            colSpan: 1,
            tfTdCssWrapper: "cell-right",
        },
    ];

    if (SubAccountTypeHelper.isOnPlatformPension(props.selectedProduct)) {
        cols.push(
            {
                title: "Single tax relief",
                key: "SingleTaxRelief",
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
                render: (data) => {
                    return MoneyDisplayHelper.FromatMoneyDisplay(data || 0, CurrencyCode.GBP, true, 2);
                }
            }
        );

        totalBody.push({
            data: MoneyDisplayHelper.FromatMoneyDisplay(singleTaxReliefTotal, CurrencyCode.GBP, true, 2),
            colSpan: 1,
            tfTdCssWrapper: "cell-right",
        });

    }

    if (props.selectedProduct !== SubAccountType.OnshoreBond && props.selectedProduct !== SubAccountType.OffshoreBond) {
        // Add regular payment
        cols.push({
            title: "Regular payment",
            key: "Regular",
            thCssWrapper: "cell-right",
            tdCssWrapper: "cell-right",
            render: (data) => {
                return MoneyDisplayHelper.FromatMoneyDisplay(data || 0, CurrencyCode.GBP, true, 2);
            },
        });
        totalBody.push({
            data: MoneyDisplayHelper.FromatMoneyDisplay(regularTotal, CurrencyCode.GBP, true, 2),
            colSpan: 1,
            tfTdCssWrapper: "cell-right",
        });
    }

    if (SubAccountTypeHelper.isOnPlatformPension(props.selectedProduct)) {
        cols.push(
            {
                title: "Regular tax relief",
                key: "RegularTaxRelief",
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
                render: (data) => {
                    return MoneyDisplayHelper.FromatMoneyDisplay(data || 0, CurrencyCode.GBP, true, 2);
                }
            }
        );

        totalBody.push({
            data: MoneyDisplayHelper.FromatMoneyDisplay(regularTaxReliefTotal, CurrencyCode.GBP, true, 2),
            colSpan: 1,
            tfTdCssWrapper: "cell-right",
        });
    }

    cols.push({
        title: "Total",
        key: "Total",
        thCssWrapper: "cell-right",
        tdCssWrapper: "cell-right",
        render: (data) => {
            return MoneyDisplayHelper.FromatMoneyDisplay(data || 0, CurrencyCode.GBP, true, 2);
        },
    });
    totalBody.push({
        data: MoneyDisplayHelper.FromatMoneyDisplay(allTotal, CurrencyCode.GBP, true, 2),
        colSpan: 1,
        tfTdCssWrapper: "cell-right",
    });

    return (
        <>
            <div className="panel-subtle mb-35">
                <HighchartsReact highcharts={Highcharts} options={options(data)} allowChartUpdate={true} />
            </div>
            <ExcelExportButton
                onRequestReport={() => {
                    return ExcelExportAction.generateContributionsReport(
                        props.subAccountHierarchyId,
                        props.fromDate,
                        props.fromInception
                    );
                }}
                selectedAccount={null}
            ></ExcelExportButton>
            <div className="panel-subtle pt-10 pb-10 mb-40">
                <Table
                    cssWrapper="table-subtle table-first-last-cell-padding-reset"
                    data={tableData}
                    col={cols}
                    TotalBody={totalBody}
                />
            </div>
        </>
    );
};

const composeTableData = (data: any) => {
    // Group by Payer first
    const groupByPayerData = groupBy(data, "Payer");


    return Object.keys(groupByPayerData).map((e) => {
        const tempObj = { Payer: e, Total: 0 };
        // Group by Type and calculator total
        const restData = groupBy(groupByPayerData[e], "Type");

        Object.keys(restData).forEach((el) => {
            if (PaymentTypeMapper[el]) {
                const totalAmount = restData[el].reduce(getReducerBasedonKey("Amount"), 0);
                tempObj[PaymentTypeMapper[el]] = totalAmount;
                tempObj["Total"] += totalAmount;

                if(PaymentTypeMapper[el]=="Single")
                {
                    const totalSingleTaxReliefAmount = restData[el].reduce(getReducerBasedonKey("SingleTaxRelief"), 0);
                    tempObj["SingleTaxRelief"] = totalSingleTaxReliefAmount;
                    tempObj["Total"] += totalSingleTaxReliefAmount
                }
                else
                {
                    const totalRegularTaxReliefAmount = restData[el].reduce(getReducerBasedonKey("RegularTaxRelief"), 0);
                    tempObj["RegularTaxRelief"] = totalRegularTaxReliefAmount;
                    tempObj["Total"] += totalRegularTaxReliefAmount
                }
            }
        });
        return tempObj;
    });
};

const getReducerBasedonKey = (key:string)=>{
    const reducer = (accumulator, currentValue) => {
        return accumulator + currentValue[key];
    };
    return reducer;
}
const GetColors = (payer: string) => {
    if (payer == PaymentPayerMapper[1])
    {
        return  "#008080";
    }
    else if(payer == PaymentPayerMapper[2])
    {
        return  "#2caffe";
    }
    else if(payer == PaymentPayerMapper[3])
    {
        return  "#808080";
    }
    else if(payer == PaymentPayerMapper[4])
    {
        return  "#CC6CE7";
    }

        return "#2caffe";
};
const composeDataSeries = (data: any): Highcharts.SeriesOptionsType[] => {
    // Group by payer type first
    let dataSeries = [];
    const groupByPayerData = groupBy(data, "Payer");
    const reducer = (accumulator, currentValue) => {
        return currentValue.Amount + accumulator;
    };
    dataSeries = Object.keys(groupByPayerData).map((e) => {
        const tempObj = {
            type: "column",
            name: PaymentPayerMapper[e],
            pointPadding: 1,
            pointWidth: 5,
            borderWidth: 0,
            data: null,
            color:GetColors(PaymentPayerMapper[e])
        };

        // Handle date format
        groupByPayerData[e].forEach((e) => {
            e.DateCompleted = new Date(JsonDateParser.getFormatDate(new Date(e.DateCompleted), "yyyy-MM")).getTime();
        });

        // Compose the rest data
        const restData = groupBy(groupByPayerData[e], "DateCompleted");
        const data = Object.keys(restData).map((el) => {
            // calculate total amount
            const totalAmount = restData[el].reduce(reducer, 0);
            return { x: parseInt(el), y: totalAmount };
        });
        tempObj.data = data;
        return tempObj;
    });
    return dataSeries;
};


const periodOptions = {
    SINCE_ACCOUNT_OPENING: "Since account opening",
    PAST_12_MONTHS: "Past 12 months",
    PAST_6_MONTHS: "Past 6 months",
    CURRENT_TAX_YEAR: "Current tax year",
    PAST_TAX_YEAR: "Past tax year"
};

export const CompletedInstructions = (props: ICompletedInstructionsProps) => {
    const [data, setData] = React.useState(null);
    const [period, setPeriod] = React.useState("Since account opening");

    React.useEffect(() => {
        const { fromDate, toDate, now } = dateHelper(period);

        ProductOverviewPaymentsInAction.getComletedProductPaymentsIn(
            props.subAccountHierarchyId,
            JsonDateParser.toJsonDateNoOffset(now),
            true,
            (data) => {
                if (!fromDate) {
                    const singlePaymentsIn = data.filter((payment: any) =>
                        payment.SubType !== PaymentSubType.TransferIn &&
                        payment.DateCompleted <= toDate
                    );
                    setData(singlePaymentsIn);
                }
                else if (!toDate) {
                    const singlePaymentsIn = data.filter((payment: any) =>
                        payment.SubType !== PaymentSubType.TransferIn &&
                        payment.DateCompleted >= fromDate
                    );
                    setData(singlePaymentsIn);
                }
                else {
                    const singlePaymentsIn = data.filter((payment: any) =>
                        payment.SubType !== PaymentSubType.TransferIn &&
                        payment.DateCompleted <= toDate &&
                        payment.DateCompleted >= fromDate
                    );
                    setData(singlePaymentsIn);
                }
            }
        );
    }, [period, props.subAccountHierarchyId]);

    const { fromDate } = dateHelper(period);
    const chartAndTableProps = { 
        subAccountHierarchyId: props.subAccountHierarchyId,
        selectedProduct: props.selectedProduct,
        fromDate: JsonDateParser.toRawDate(fromDate ?? new Date()),
        fromInception: period === periodOptions.SINCE_ACCOUNT_OPENING 
    }

    return (
        <>
            <h2 className="mb-24">Completed instructions</h2>
            <SelectEditor
                cssWrapper="control-size-245"
                options={Object.values(periodOptions)}
                getText={(v) => {
                    return v;
                }}
                selected={period}
                onChange={(v) => {
                    setPeriod(v);
                }}
                label="Date"
            />
            {(props.selectedProduct === SubAccountType.Npr || props.selectedProduct === SubAccountType.JuniorSipp) && <h3 className="mb-5">Regular and single payments</h3>}
            {data?.length > 0 ? (
                renderChartAndTable(data, chartAndTableProps)
            ) : (
                <div className="panel-subtle">No payments in over the date range selected</div>
            )}
        </>
    );
};

const dateHelper = (period: string) => {
    // calculate start of current tax year
    const now = new Date();
    const nowStr = JsonDateParser.getFormatDateString(now, "yyyy/MM/dd").split("/");
    const pointDate = new Date(Number(nowStr[0]), 3, 6);
    const currentTaxYearStartDate = now > pointDate
        ? new Date(Number(nowStr[0]), 3, 6)
        : new Date(Number(nowStr[0]) - 1, 3, 6);

    // calculate start and end of previous tax year
    const pastTaxYearEndDate = addDays(currentTaxYearStartDate, -1);
    const pastTaxYearStartDate = addYears(currentTaxYearStartDate, -1);

    // Re-call the api and re-render the chart and table
    let toDate =  period == periodOptions.PAST_TAX_YEAR ? 
        JsonDateParser.toJsonDateNoOffset(pastTaxYearEndDate) : 
        JsonDateParser.toJsonDateNoOffset(now);
    let fromDate = null;

    switch (period) {
        case periodOptions.SINCE_ACCOUNT_OPENING:
            break;
        case periodOptions.PAST_12_MONTHS:
            fromDate = JsonDateParser.toJsonDateNoOffset(addYears(new Date(), -1));
            break;
        case periodOptions.PAST_6_MONTHS:
            fromDate = JsonDateParser.toJsonDateNoOffset(addMonths(new Date(), -6));
            break;
        case periodOptions.CURRENT_TAX_YEAR:
            fromDate = JsonDateParser.toJsonDateNoOffset(currentTaxYearStartDate);
            break;
        case periodOptions.PAST_TAX_YEAR:
            fromDate = JsonDateParser.toJsonDateNoOffset(pastTaxYearStartDate);
            break;
    }

    return { fromDate, toDate, now };
}
